import { useRef, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { v4 as uuidv4 } from "uuid";

import { SanctionEventDetail } from "../../../api/sanctionEvents";
import ManagementModal from "../../../components/ui/modals/ManagementModal";
import SelectDropdownV2 from "../../../components/ui/selectdropdown/v2";
import { OptionNameValuePair } from "../../../lib/interfaces/types";
import { LAKH } from "../../../lib/utils";

const toLakhs = (amount?: number | null) =>
    amount === null || amount === undefined ? "" : String(amount / LAKH);

const fromLakhs = (amountInLakhs: string) => {
    if (amountInLakhs === "") return 0;

    const numericValue = Number(amountInLakhs);
    return Number.isFinite(numericValue) ? numericValue * LAKH : 0;
};

interface SanctionEventDetailsModalProps {
    title: string;
    details: SanctionEventDetail[];
    loanAccountOptions: OptionNameValuePair<string>[];
    disabled?: boolean;
    onClose: () => void;
    onApply: (details: SanctionEventDetail[]) => void;
}

export const SanctionEventDetailsModal: React.FC<
    SanctionEventDetailsModalProps
> = ({
    title,
    details,
    loanAccountOptions,
    disabled = false,
    onClose,
    onApply,
}) => {
    const [localDetails, setLocalDetails] = useState<SanctionEventDetail[]>(
      () => {
        if (details.length > 0) return details.map((detail) => ({ ...detail }));
        if (loanAccountOptions.length === 0) return [];
        return [
          {
            detail_id: uuidv4(),
            loan_account_number: "",
            sanction_amount: 0,
          },
        ];
      },
    );
    const initialDetailIds = useRef(
      new Set(details.map((detail) => detail.detail_id)),
    );

    const hasNoLoanAccounts = loanAccountOptions.length === 0;
    const hasIncompleteRow = localDetails.some(
      (detail) => detail.loan_account_number === "",
    );
    const isDoneDisabled = disabled || hasNoLoanAccounts || hasIncompleteRow;

    const addDetail = () => {
      setLocalDetails((prev) => [
        ...prev,
        {
          detail_id: uuidv4(),
          loan_account_number: "",
          sanction_amount: 0,
        },
      ]);
    };

    const updateDetail = (
      rowIndex: number,
      field: keyof SanctionEventDetail,
      value: string | number,
    ) => {
      setLocalDetails((prev) =>
        prev.map((detail, idx) =>
          idx === rowIndex ? { ...detail, [field]: value } : detail,
        ),
      );
    };

    const deleteDetail = (rowIndex: number) => {
      setLocalDetails((prev) => prev.filter((_, idx) => idx !== rowIndex));
    };

    const applyChanges = () => {
      onApply(localDetails);
      onClose();
    };

    const handleClose = () => {
      if (disabled) return;
      onClose();
    };

    return (
      <ManagementModal
        title={title}
        submitLabel="Done"
        onSubmit={applyChanges}
        onCancel={handleClose}
        onClose={handleClose}
        submitEnabled={!isDoneDisabled}
        className="w-full max-w-[640px]"
      >
        <div className="flex flex-col gap-3">
          <div>
            <span className="text-xs font-medium leading-none text-[#737373]">
              Facilities
            </span>
          </div>

          <div className="max-h-[360px] overflow-y-auto">
            <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_36px] items-center gap-x-4 gap-y-3">
              <span className="text-sm font-medium text-[#0A0A0A]">
                Loan Account
              </span>
              <span className="text-sm font-medium text-[#0A0A0A]">
                Amount (in ₹ Lakhs)
              </span>
              <span />

              {localDetails.map((detail, rowIndex) => (
                <div key={detail.detail_id} className="contents">
                  <SelectDropdownV2
                    name="Loan Account"
                    placeholder="Search loan account"
                    options={loanAccountOptions}
                    selectedOption={loanAccountOptions.find(
                      (option) => option.value === detail.loan_account_number,
                    )}
                    disabled={disabled || loanAccountOptions.length === 0}
                    onSelectionChange={(option) =>
                      updateDetail(
                        rowIndex,
                        "loan_account_number",
                        option.value,
                      )
                    }
                    containerClassName="w-full min-w-0"
                    labelClassName={`h-9 rounded-[8px] border border-[#E5E5E5] bg-white px-3 text-sm text-[#0A0A0A] shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] outline-none ${
                      disabled || loanAccountOptions.length === 0
                        ? "cursor-not-allowed bg-gray-50 opacity-70"
                        : "focus:border-[#3551F3]"
                    }`}
                    dropdownClassName="min-w-[240px]"
                    showOptionTooltip
                  />
                  <input
                    type="number"
                    value={toLakhs(detail.sanction_amount)}
                    disabled={disabled}
                    onChange={(event) =>
                      updateDetail(
                        rowIndex,
                        "sanction_amount",
                        fromLakhs(event.target.value),
                      )
                    }
                    className="h-9 rounded-[8px] border border-[#E5E5E5] bg-white px-3 text-sm text-[#0A0A0A] shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] outline-none focus:border-[#3551F3] disabled:bg-gray-50"
                  />
                  <button
                    type="button"
                    disabled={disabled || initialDetailIds.current.has(detail.detail_id)}
                    onClick={() => deleteDetail(rowIndex)}
                    className="flex h-9 w-9 items-center justify-center rounded-md text-[#737373] hover:text-red-600 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Delete facility row"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>

            {localDetails.length === 0 && (
              <div className="py-6 text-center text-xs text-[#737373]">
                {hasNoLoanAccounts
                  ? "No loan account for this facility category available in this case"
                  : "No facility rows added"}
              </div>
            )}
          </div>

          <div>
            <button
              type="button"
              disabled={disabled || hasNoLoanAccounts}
              onClick={addDetail}
              className="inline-flex items-center gap-2 text-sm font-normal text-[#737373] focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus size={16} className="shrink-0" />
              Add
            </button>
          </div>
        </div>
      </ManagementModal>
    );
};
